/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { Children, createContext, type ForwardedRef, forwardRef, isValidElement, type ReactElement } from 'react';
import type { SlotProps } from 'react-aria-components';
import { type ContextValue, DEFAULT_SLOT, MenuItem as MenuItemCore, type MenuItemProps as MenuItemCoreProps, Provider, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { CheckboxContext } from '../checkbox/checkbox.component';
import { IconContext } from '../icon/icon.component';
import { NumberBadgeContext } from '../number-badge/number-badge.component';
import { Text, TextContext } from '../text/text.component';

interface MenuItemProps<T> extends Omit<MenuItemCoreProps<T>, 'style' | 'className'>, SlotProps {
  /** The size of the menu item. (Default: 40) */
  size?: keyof typeof sizeVariants;
  /** Whether the menu item is critical. (Default: false) */
  isCritical?: boolean;
  style?: stylex.StyleXStyles;
}

const MenuItemContext = createContext<ContextValue<MenuItemProps<object>, HTMLDivElement>>({});

const MenuItem = forwardRef((props: MenuItemProps<object>, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, MenuItemContext);

  const { style, size = 40, isCritical = false, ...coreProps } = props;

  return (
    <MenuItemCore
      ref={ref}
      {...stylex.props(styles.base, sizeVariants[size], props.isCritical && criticalStyles.base, style)}
      {...coreProps}>
      {(renderProps) => {
        const resolvedChildren = typeof coreProps.children === 'function' ? coreProps.children(renderProps) : coreProps.children;

        const isTextOnly = Children.toArray(resolvedChildren).every(c => !isValidElement(c));

        return (
          <Provider values={[
            [TextContext, {
              slots: {
                [DEFAULT_SLOT]: {
                  size: size >= 40 ? 16 : 14,
                  style: [textStyles.base, styles.firstRow],
                  color: (
                    (renderProps.isDisabled && 'disabled') ||
                    (isCritical && 'critical') ||
                    ((renderProps.isHovered || renderProps.isSelected) && 'primary') ||
                    'secondary'
                  ),
                  weight: 'regular',
                },
                [MENU_ITEM_SLOTS.LABEL]: {
                  size: size >= 40 ? 16 : 14,
                  style: [textStyles.base, styles.firstRow, styles.fullWidth],
                  color: (
                    (renderProps.isDisabled && 'disabled') ||
                    (isCritical && 'critical') ||
                    ((renderProps.isHovered || renderProps.isSelected) && 'primary') ||
                    'secondary'
                  ),
                  weight: renderProps.isSelected ? 'medium' : 'regular',
                },
                [MENU_ITEM_SLOTS.DESCRIPTION]: {
                  size: 12,
                  style: [textStyles.base, styles.secondRow],
                  color: (
                    (renderProps.isDisabled && 'disabled') ||
                    'secondary'
                  ),
                  weight: 'regular',
                }
              },
            }],
            [NumberBadgeContext, {
              size: size >= 40 ? 20 : 16,
              color: 'critical',
              style: styles.firstRow,
            }],
            [IconContext, {
              slots: {
                [DEFAULT_SLOT]: {
                  size: size >= 40 ? 20 : 16,
                  color: (
                    (renderProps.isDisabled && 'disabled') ||
                    (isCritical && 'critical') ||
                    ((renderProps.isHovered || renderProps.isSelected) && 'primary') ||
                    'secondary'
                  ),
                  style: styles.firstRow,
                }
              }
            }],
            [CheckboxContext, {
              slots: {
                [DEFAULT_SLOT]: {
                  size: size >= 40 ? 20 : 16,
                  style: styles.firstRow,
                  isSelected: renderProps.isSelected,
                },
                [MENU_ITEM_SLOTS.LABEL]: {
                  size: size >= 40 ? 20 : 16,
                  style: [styles.firstRow, styles.fullWidth],
                  isSelected: renderProps.isSelected,
                }
              }
            }],
          ]}>
            {isTextOnly ? <Text slot={MENU_ITEM_SLOTS.LABEL}>{resolvedChildren}</Text> : resolvedChildren}
          </Provider>
        );
      }}
    </MenuItemCore>
  );
}) as <T>(p: MenuItemProps<T> & { ref?: ForwardedRef<HTMLDivElement> }) => ReactElement;

const styles = stylex.create({
  base: {
    borderRadius: 8,
    textDecoration: 'none',
    alignItems: 'center',
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered])': $.opacity04,
    },
    boxSizing: 'border-box',
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
    },
    display: 'grid',
    gridRowGap: 4,
    gridTemplateColumns: 'auto 1fr auto',
    justifyItems: 'flex-start',
    outlineColor: {
      default: 'transparent',
      ':is([data-focus-visible])': $.borderInteractive,
    },
    width: '100%',
  },
  firstRow: { gridArea: '1' },
  secondRow: { gridArea: '2' },
  fullWidth: { gridColumn: 'span 3' },
});

const textStyles = stylex.create({
  base: {
    textWrap: 'nowrap',
  },
});

const criticalStyles = stylex.create({
  base: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered])': $.surfaceCriticalSilent,
    },
  }
});

const sizeVariants = stylex.create({
  48: {
    paddingBlock: 6.5,
    paddingInline: 12,
    gridColumnGap: 12,
    height: 48,
  },
  42: {
    paddingBlock: 4,
    paddingInline: 8,
    gridColumnGap: 8,
    height: 42,
  },
  40: {
    paddingBlock: 10,
    paddingInline: 12,
    gridColumnGap: 12,
    height: 40,
  },
  36: {
    paddingBlock: 9,
    paddingInline: 8,
    gridColumnGap: 8,
    height: 36,
  },
});

const MENU_ITEM_SLOTS = {
  LABEL: "label",
  DESCRIPTION: "description",
};

export { MENU_ITEM_SLOTS, MenuItem, MenuItemContext, type MenuItemProps };
